import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

type PaidOutRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   transaction: string;
   transactionCode: string;
   transactionDescription: string;
   reason: string;
   receipt: string;
   amount: string;
   amountRaw: number;
   user: string;
   hasRealTyallsRowId: boolean;
};

const PAID_OUT_TRANSACTION_DESCRIPTIONS: Record<string, string> = {
   '8000': 'Paid Out',
   '8001': 'Gratuity',
   '8010': 'Paid Out'
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function getPathValue(source: any, path: string): any {
   if (!source || !path) return undefined;

   return path.split('.').reduce((current, part) => current?.[part], source);
}

function getFirstValue(source: any, paths: string[]): any {
   for (const path of paths) {
      const value = getPathValue(source, path);

      if (value !== null && value !== undefined && value !== '') {
         return value;
      }
   }

   return undefined;
}

function getFirstString(source: any, paths: string[]): string {
   return asString(getFirstValue(source, paths));
}

function toNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/^-\s*/, '-');

   const amount = Number(cleaned);
   return Number.isFinite(amount) ? amount : null;
}

function getFirstNumber(source: any, paths: string[]): number | null {
   for (const path of paths) {
      const amount = toNumberOrNull(getPathValue(source, path));

      if (amount !== null) return amount;
   }

   return null;
}

function formatCurrency(value: number): string {
   const formatted = Math.abs(value).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
   if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(
         2,
         '0'
      )}/${slashMatch[3]}`;
   }

   return raw;
}

function formatTime(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoTimeMatch = raw.match(/T(\d{2}:\d{2})/);
   if (isoTimeMatch) return isoTimeMatch[1];

   const simpleTimeMatch = raw.match(/^(\d{1,2}):(\d{2})/);
   if (simpleTimeMatch) {
      return `${simpleTimeMatch[1].padStart(2, '0')}:${simpleTimeMatch[2]}`;
   }

   return raw;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      getFirstString(row, [
         'ReceiptNumber',
         'receiptNumber',
         'ReceiptNo',
         'receiptNo',
         'TransactionNo',
         'transactionNo',
         'Id',
         'id'
      ]) ||
      `paid-out:${getFirstString(row, ['Date', 'date'])}:${getFirstString(row, [
         'RoomNumber',
         'roomNumber'
      ])}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function getSourceRows(reportJson: any): { rows: any[]; isOracle: boolean } {
   const parsed = parseReportJson(reportJson);

   if (Array.isArray(parsed?.PaidOuts)) {
      return { rows: parsed.PaidOuts, isOracle: false };
   }

   if (Array.isArray(parsed?.PaidOutsReport)) {
      return { rows: parsed.PaidOutsReport, isOracle: false };
   }

   if (Array.isArray(parsed?.Rows)) {
      return { rows: parsed.Rows, isOracle: false };
   }

   if (Array.isArray(parsed?.rows)) {
      return { rows: parsed.rows, isOracle: false };
   }

   if (Array.isArray(parsed?.Postings)) {
      return { rows: parsed.Postings, isOracle: false };
   }

   if (Array.isArray(parsed?.financialPostings)) {
      return { rows: parsed.financialPostings, isOracle: true };
   }

   if (Array.isArray(parsed?.journalPostings?.postings)) {
      return { rows: parsed.journalPostings.postings, isOracle: true };
   }

   if (Array.isArray(parsed?.JournalPostings?.Postings)) {
      return { rows: parsed.JournalPostings.Postings, isOracle: true };
   }

   return { rows: [], isOracle: false };
}

/**
 * Expected saved Tyalls shape:
 * {
 *   Debit: 25.00,
 *   Credit: 0
 * }
 *
 * Paid Out rule:
 * - positive Debit = paid out amount
 * - positive Amount = paid out amount
 * - negative Credit can also be treated as paid out and displayed as a positive amount
 */
function amountFromMappedPostingDollars(row: any): number | null {
   const debit = getFirstNumber(row, [
      'Debit',
      'debit',
      'DebitAmount',
      'debitAmount',
      'DebitAmount.Amount',
      'debitAmount.amount'
   ]);

   if (debit !== null && debit > 0) {
      return debit;
   }

   const amount = getFirstNumber(row, [
      'Amount',
      'amount',
      'AmountRaw',
      'amountRaw',
      'PaidOutAmount',
      'paidOutAmount'
   ]);

   if (amount !== null && amount > 0) {
      return amount;
   }

   const credit = getFirstNumber(row, [
      'Credit',
      'credit',
      'CreditAmount',
      'creditAmount',
      'CreditAmount.Amount',
      'creditAmount.amount'
   ]);

   if (credit !== null && credit < 0) {
      return Math.abs(credit);
   }

   return null;
}

function amountFromOraclePostingDollars(row: any): number | null {
   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount > 0) {
      return debitAmount;
   }

   const amount = getFirstNumber(row, [
      'amount.amount',
      'postedAmount.amount',
      'Amount',
      'amount'
   ]);

   if (amount !== null && amount > 0) {
      return amount;
   }

   const creditAmount = getFirstNumber(row, [
      'creditAmount.amount',
      'CreditAmount.Amount',
      'CreditAmount',
      'creditAmount'
   ]);

   if (creditAmount !== null && creditAmount < 0) {
      return Math.abs(creditAmount);
   }

   return null;
}

function getDate(row: any): string {
   return getFirstString(row, [
      'Date',
      'date',
      'TransactionDate',
      'transactionDate',
      'PostingDate',
      'postingDate',
      'BusinessDate',
      'businessDate'
   ]);
}

function getTime(row: any): string {
   return getFirstString(row, [
      'Time',
      'time',
      'TransactionTime',
      'transactionTime',
      'lastModifyDateTime',
      'LastModifyDateTime'
   ]);
}

function getRoom(row: any): string {
   return getFirstString(row, [
      'RoomNumber',
      'roomNumber',
      'RoomNo',
      'roomNo',
      'Room',
      'room',
      'guestInfo.roomId',
      'guestInfo.roomNumber'
   ]);
}

function getGuestName(row: any): string {
   return getFirstString(row, [
      'GuestName',
      'guestName',
      'Name',
      'name',
      'guestInfo.guestName'
   ]);
}

function getTransactionCode(row: any): string {
   return getFirstString(row, [
      'TransactionCode',
      'transactionCode',
      'TrnCode',
      'trnCode'
   ]);
}

function getTransactionDescription(row: any): string {
   const transactionCode = getTransactionCode(row);

   return (
      getFirstString(row, [
         'TransactionCodeDescription',
         'transactionCodeDescription',
         'TransactionDescription',
         'transactionDescription',
         'CodeDescription',
         'codeDescription'
      ]) ||
      PAID_OUT_TRANSACTION_DESCRIPTIONS[transactionCode] ||
      getFirstString(row, ['Description', 'description'])
   );
}

function getTransactionDisplay(row: any): string {
   const code = getTransactionCode(row);
   const description = getTransactionDescription(row);

   if (code && description) return `${description} (${code})`;
   if (description) return description;
   return code;
}

function getReason(row: any): string {
   const values = [
      getFirstString(row, [
         'Reason',
         'reason',
         'Description',
         'description',
         'Remark',
         'remark',
         'Reference',
         'reference',
         'TransactionRemark',
         'transactionRemark'
      ])
   ].filter(Boolean);

   return Array.from(new Set(values)).join(' ');
}

function getReceipt(row: any): string {
   const receipt = getFirstString(row, [
      'ReceiptNumber',
      'receiptNumber',
      'ReceiptNo',
      'receiptNo',
      'TransactionNo',
      'transactionNo',
      'CheckNumber',
      'checkNumber'
   ]);

   if (!receipt) return '';

   return receipt.startsWith('#') ? receipt : `#${receipt}`;
}

function getUser(row: any): string {
   const direct = getFirstString(row, [
      'User',
      'user',
      'CashId',
      'cashId',
      'PostedByCashier',
      'postedByCashier',
      'CashierId',
      'cashierId',
      'cashierInfo.cashierId'
   ]);

   if (direct) return direct;

   return getFirstString(row, ['UserName', 'userName', 'cashierInfo.userName']);
}

function normalisePaidOutRows(sourceRows: any[], isOracle: boolean): PaidOutRow[] {
   const rows: PaidOutRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw <= 0) continue;

      const dateRaw = getDate(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         date: formatDate(dateRaw),
         dateRaw,
         time: formatTime(getTime(sourceRow)),
         room: getRoom(sourceRow),
         guestName: getGuestName(sourceRow),
         transaction: getTransactionDisplay(sourceRow),
         transactionCode: getTransactionCode(sourceRow),
         transactionDescription: getTransactionDescription(sourceRow),
         reason: getReason(sourceRow),
         receipt: getReceipt(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getUser(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function sortPaidOutRows(rows: PaidOutRow[]): PaidOutRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);
      if (dateCompare !== 0) return dateCompare;

      return a.time.localeCompare(b.time);
   });
}

export const PAID_OUTS_DEFINITION: ReportDefinition = {
   reportType: 'PaidOuts',
   version: 2,
   title: 'Paid Outs',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Paid Outs Report',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'paidOut'
         );

         const totalPaidOut = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['amountRaw']);
            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length
            },
            {
               label: 'Total Paid Out',
               value: formatCurrency(totalPaidOut),
               tone: totalPaidOut < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['paidOut']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['paidOut'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'transaction',
         header: 'TRANSACTION',
         width: '190px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'receipt',
         header: 'RECEIPT',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'transaction', label: 'Transaction', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         { key: 'receipt', label: 'Receipt', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: (reportJson: any): ReportRowVm[] => {
      const { rows: sourceRows, isOracle } = getSourceRows(reportJson);

      const paidOutRows = sortPaidOutRows(normalisePaidOutRows(sourceRows, isOracle));

      return paidOutRows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'paidOut',
            date: row.date,
            dateRaw: row.dateRaw,
            time: row.time,
            room: row.room,
            guestName: row.guestName,
            transaction: row.transaction,
            transactionCode: row.transactionCode,
            transactionDescription: row.transactionDescription,
            reason: row.reason,
            receipt: row.receipt,
            amount: row.amount,
            amountRaw: row.amountRaw,
            user: row.user
         }
      }));
   }
};
